import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { useRouter } from '../hooks/useRouter';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/oV66CvE5T9DxmmC6cXmgrm/ChatGPT_Image_Mar_27__2026__07_43_24_PM-removebg-preview.png';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [careersOpen, setCareersOpen] = useState(false);
  const {
    currentRoute,
    navigate
  } = useRouter();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);
  const handleNavClick = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };
  const isActive = (path: string) => {
    if (path === '/' && (currentRoute === '/' || currentRoute === '')) return true;
    if (path !== '/' && currentRoute.startsWith(path)) return true;
    return false;
  };
  const isAboutActive = isActive('/about') || isActive('/testimonials') || isActive('/faq');
  const isCareersActive = isActive('/employment');
  const services = [{
    label: 'Wound Care & Management',
    path: '/services/wound-care'
  }, {
    label: 'Tracheostomy (Trac) Care',
    path: '/services/tracheostomy'
  }, {
    label: 'Enteral Nutrition (G-Tube Care)',
    path: '/services/g-tube'
  }, {
    label: 'Colostomy & Ostomy Care',
    path: '/services/colostomy-care'
  }, {
    label: 'Foley Catheter Management',
    path: '/services/foley-care'
  }, {
    label: 'Respite & Holiday Care',
    path: '/services/respite-care'
  }, {
    label: 'Planning & Funding Care',
    path: '/services/planning-funding'
  }, {
    label: 'Concierge Services',
    path: '/services/concierge'
  }, {
    label: 'In-home Care Services',
    path: '/services/in-home-care'
  }, {
    label: 'Medication Reminders',
    path: '/services/medication-reminders'
  }, {
    label: 'Occupational Therapy',
    path: '/services/occupational-therapy'
  }, {
    label: 'Personal Care Services',
    path: '/services/personal-care'
  }, {
    label: 'Physical Therapy',
    path: '/services/physical-therapy'
  }];
  const aboutItems = [{
    label: 'About Us',
    path: '/about'
  }, {
    label: 'Testimonials',
    path: '/testimonials'
  }, {
    label: 'FAQs',
    path: '/faq'
  }];
  const careersItems = [{
    label: 'Employment Application',
    path: '/employment'
  }, {
    label: 'Application Form',
    path: '/application-form'
  }];
  const navLinkClass = (path: string) => `whitespace-nowrap px-3 lg:px-4 py-2 text-sm font-bold transition-colors uppercase tracking-wider rounded-md ${isActive(path) ? 'text-white bg-white/10' : 'text-gray-300 hover:text-white hover:bg-white/5'}`;
  const dropdownBtnClass = (active: boolean) => `whitespace-nowrap flex items-center px-3 lg:px-4 py-2 text-sm font-bold transition-colors uppercase tracking-wider rounded-md ${active ? 'text-white bg-white/10' : 'text-gray-300 hover:text-white hover:bg-white/5'}`;
  return <>
      <header className="fixed top-0 left-0 right-0 z-[60] flex flex-col transition-all duration-300 shadow-lg" data-id="element-230">
        {/* Top Tier: Logo (Green with dot pattern) */}
        <div className={`bg-primary transition-all duration-300 overflow-hidden ${isScrolled ? 'h-0 opacity-0 py-0' : 'py-2 lg:py-5 opacity-100'}`} style={{
        backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.15) 2px, transparent 2px)',
        backgroundSize: '20px 20px'
      }} data-id="element-231">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between" data-id="element-232">
            <div className="flex items-center cursor-pointer group flex-shrink-0" onClick={() => handleNavClick('/')} data-id="element-233">
              <img src={LOGO_URL} alt="Edvin Home Health Care Solution and Nursing Services Logo" className="h-12 w-12 lg:h-20 lg:w-20 object-contain mr-2 lg:mr-4 bg-white rounded-full p-1 shadow-md" data-id="element-234" />
              <div className="flex flex-col whitespace-nowrap leading-tight" data-id="element-235">
                <span className="text-base lg:text-2xl font-bold text-white tracking-widest uppercase drop-shadow-md" style={{
                fontFamily: "'Cinzel', serif"
              }} data-id="element-236">
                  Edvin Home
                </span>
                <span className="text-[10px] lg:text-sm font-semibold text-white/90 tracking-[0.1em] uppercase mt-0.5 drop-shadow-sm" data-id="element-237">
                  Health Care Solution & Nursing Services
                </span>
              </div>
            </div>

            {/* Mobile Menu Button (when not scrolled) */}
            <div className="lg:hidden flex items-center" data-id="element-238">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white hover:text-white/80 p-2 focus:outline-none" data-id="element-239">
                {mobileMenuOpen ? <XIcon className="h-7 w-7" data-id="element-240" /> : <MenuIcon className="h-7 w-7" data-id="element-241" />}
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Tier: Navigation (Dark grey with red bottom border) */}
        <div className="bg-dark border-b-4 border-accent transition-all duration-300" data-id="element-242">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-243">
            <div className={`flex justify-between items-center transition-all duration-300 ${isScrolled ? 'h-14 lg:overflow-visible' : 'h-0 lg:h-14 overflow-hidden lg:overflow-visible'}`} data-id="element-244">
              {/* Mini Logo (Visible only when scrolled) */}
              <div className={`flex items-center cursor-pointer overflow-hidden transition-all duration-300 ${isScrolled ? 'w-auto opacity-100 mr-6' : 'w-0 opacity-0 mr-0'}`} onClick={() => handleNavClick('/')} data-id="element-245">
                <img src={LOGO_URL} className="h-10 w-10 object-contain bg-white rounded-full p-0.5 mr-2" alt="Logo" data-id="element-246" />
                <span className="text-white font-bold text-sm hidden sm:block whitespace-nowrap" style={{
                fontFamily: "'Cinzel', serif"
              }} data-id="element-247">
                  EDVIN HOME
                </span>
              </div>

              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center space-x-1 flex-grow" data-id="element-248">
                <button onClick={() => handleNavClick('/')} className={navLinkClass('/')} data-id="element-249">
                  Home
                </button>

                {/* About Dropdown */}
                <div className="relative group" data-id="element-250">
                  <button onClick={() => handleNavClick('/about')} className={dropdownBtnClass(isAboutActive)} data-id="element-251">
                    About Us
                    <ChevronDownIcon className="ml-1 h-4 w-4 group-hover:rotate-180 transition-transform duration-200" data-id="element-252" />
                  </button>
                  <div className="absolute left-0 mt-2 w-52 bg-white rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top-left border border-gray-100" data-id="element-253">
                    <div className="py-2" data-id="element-254">
                      {aboutItems.map((item, idx) => <button key={idx} onClick={() => handleNavClick(item.path)} className={`block w-full text-left px-4 py-2.5 text-sm transition-colors ${isActive(item.path) ? 'text-primary bg-primary/5 font-semibold' : 'text-dark-muted hover:bg-primary/5 hover:text-primary'}`} data-id="element-255">
                          {item.label}
                        </button>)}
                    </div>
                  </div>
                </div>

                {/* Services Dropdown */}
                <div className="relative group" data-id="element-256">
                  <button onClick={() => handleNavClick('/services')} className={dropdownBtnClass(isActive('/services'))} data-id="element-257">
                    Services
                    <ChevronDownIcon className="ml-1 h-4 w-4 group-hover:rotate-180 transition-transform duration-200" data-id="element-258" />
                  </button>
                  <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top-left border border-gray-100" data-id="element-259">
                    <div className="py-2 grid grid-cols-1 max-h-96 overflow-y-auto" data-id="element-260">
                      {services.map((service, idx) => <button key={idx} onClick={() => handleNavClick(service.path)} className="text-left px-4 py-2.5 text-sm text-dark-muted hover:bg-primary/5 hover:text-primary transition-colors" data-id="element-261">
                          {service.label}
                        </button>)}
                    </div>
                  </div>
                </div>

                <button onClick={() => handleNavClick('/resources')} className={navLinkClass('/resources')} data-id="element-262">
                  Resources
                </button>

                {/* Careers Dropdown */}
                <div className="relative group" data-id="element-263">
                  <button onClick={() => handleNavClick('/employment')} className={dropdownBtnClass(isCareersActive)} data-id="element-264">
                    Careers
                    <ChevronDownIcon className="ml-1 h-4 w-4 group-hover:rotate-180 transition-transform duration-200" data-id="element-265" />
                  </button>
                  <div className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top-left border border-gray-100" data-id="element-266">
                    <div className="py-2" data-id="element-267">
                      {careersItems.map((item, idx) => <button key={idx} onClick={() => handleNavClick(item.path)} className="block w-full text-left px-4 py-2.5 text-sm text-dark-muted hover:bg-primary/5 hover:text-primary transition-colors" data-id="element-268">
                          {item.label}
                        </button>)}
                    </div>
                  </div>
                </div>

                <button onClick={() => handleNavClick('/contact')} className="ml-auto whitespace-nowrap bg-accent hover:bg-red-700 text-white px-6 py-2 rounded-md text-sm font-bold uppercase tracking-wider transition-all shadow-sm" data-id="element-269">
                  Contact Us
                </button>
              </nav>

              {/* Mobile Menu Button (when scrolled) */}
              <div className={`lg:hidden flex items-center ml-auto transition-all duration-300 ${isScrolled ? 'opacity-100 w-auto' : 'opacity-0 w-0 overflow-hidden'}`} data-id="element-270">
                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white hover:text-primary-light p-2 focus:outline-none" data-id="element-271">
                  {mobileMenuOpen ? <XIcon className="h-7 w-7" data-id="element-272" /> : <MenuIcon className="h-7 w-7" data-id="element-273" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div className={`lg:hidden fixed inset-0 bg-white z-50 overflow-y-auto transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'} ${isScrolled ? 'pt-[60px]' : 'pt-[90px]'}`} data-id="element-274">
        <div className="flex flex-col px-6 pb-8 pt-4 space-y-4" data-id="element-275">
          <button onClick={() => handleNavClick('/')} className={`text-left text-lg font-medium py-2 border-b border-gray-100 ${isActive('/') ? 'text-primary' : 'text-dark'}`} data-id="element-276">
            Home
          </button>

          {/* About Accordion */}
          <div className="py-2 border-b border-gray-100" data-id="element-277">
            <button onClick={() => setAboutOpen(!aboutOpen)} className={`flex justify-between items-center w-full text-lg font-medium ${isAboutActive ? 'text-primary' : 'text-dark'}`} data-id="element-278">
              About
              <ChevronDownIcon className={`h-5 w-5 transition-transform ${aboutOpen ? 'rotate-180' : ''}`} data-id="element-279" />
            </button>
            {aboutOpen && <div className="mt-3 pl-4 flex flex-col space-y-3" data-id="element-280">
                {aboutItems.map((item, idx) => <button key={idx} onClick={() => handleNavClick(item.path)} className={`text-left ${isActive(item.path) ? 'text-primary font-semibold' : 'text-dark-muted'}`} data-id="element-281">
                    {item.label}
                  </button>)}
              </div>}
          </div>

          {/* Services Accordion */}
          <div className="py-2 border-b border-gray-100" data-id="element-282">
            <button onClick={() => setServicesOpen(!servicesOpen)} className={`flex justify-between items-center w-full text-lg font-medium ${isActive('/services') ? 'text-primary' : 'text-dark'}`} data-id="element-283">
              Services
              <ChevronDownIcon className={`h-5 w-5 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} data-id="element-284" />
            </button>
            {servicesOpen && <div className="mt-3 pl-4 flex flex-col space-y-3" data-id="element-285">
                {services.map((service, idx) => <button key={idx} onClick={() => handleNavClick(service.path)} className="text-left text-dark-muted" data-id="element-286">
                    {service.label}
                  </button>)}
              </div>}
          </div>

          <button onClick={() => handleNavClick('/resources')} className={`text-left text-lg font-medium py-2 border-b border-gray-100 ${isActive('/resources') ? 'text-primary' : 'text-dark'}`} data-id="element-287">
            Resources
          </button>

          {/* Careers Accordion */}
          <div className="py-2 border-b border-gray-100" data-id="element-288">
            <button onClick={() => setCareersOpen(!careersOpen)} className={`flex justify-between items-center w-full text-lg font-medium ${isCareersActive ? 'text-primary' : 'text-dark'}`} data-id="element-289">
              Careers
              <ChevronDownIcon className={`h-5 w-5 transition-transform ${careersOpen ? 'rotate-180' : ''}`} data-id="element-290" />
            </button>
            {careersOpen && <div className="mt-3 pl-4 flex flex-col space-y-3" data-id="element-291">
                {careersItems.map((item, idx) => <button key={idx} onClick={() => handleNavClick(item.path)} className="text-left text-dark-muted" data-id="element-292">
                    {item.label}
                  </button>)}
              </div>}
          </div>

          <button onClick={() => handleNavClick('/contact')} className="mt-6 bg-primary text-white px-6 py-3 rounded-lg font-semibold text-center shadow-md" data-id="element-293">
            Contact Us Today
          </button>
        </div>
      </div>
    </>;
}